import { useState, useCallback, useMemo } from 'react';
import { useConfig } from '../../contexts/ConfigContext';
import { useLog } from '../../contexts/LogContext';
import { api } from '../../utils/api';
import { buildLayoutImportPayload } from '../../utils/buildLayoutImportPayload';
import { JsonFileInput } from '../../components/common/JsonFileInput';
import { DataSummary } from '../../components/common/DataSummary';
import type { SummaryItem } from '../../components/common/DataSummary/types';
import type { FloorPlanRecord } from '../EditFloorPlanContainer/types';
import type {
  LayoutExportJson,
  LayoutImportResult,
} from '../../types/layout.types';
import type { ImportLayoutContainerProps, LoadedFileInfo } from './types';

const LAYOUT_SCHEMA = 'monco.layout_export';

const inputClass =
  'bg-[#0d1117] border border-[#30363d] text-[#c9d1d9] px-2.5 py-2 rounded-md text-sm font-mono focus:outline-none focus:border-[#58a6ff]';
const buttonClass =
  'px-5 py-2.5 rounded-md font-semibold text-sm text-white cursor-pointer transition-opacity disabled:opacity-50 disabled:cursor-not-allowed';

const describeLayout = (layoutExport: LayoutExportJson): string => {
  const units = layoutExport.units ?? {};
  const scale = units.scale
    ? `scale 1:${units.scale}${units.scale_verified ? '' : ' (unverified)'}`
    : 'no scale';
  return [
    `status: ${String(layoutExport.status)}`,
    `${layoutExport.zones?.length ?? 0} zone(s)`,
    `${layoutExport.rooms?.length ?? 0} room(s)`,
    `${layoutExport.walls?.length ?? 0} wall(s)`,
    `${layoutExport.doors?.length ?? 0} door(s)`,
    `${layoutExport.windows?.length ?? 0} window(s)`,
    scale,
  ].join(' | ');
};

export const ImportLayoutContainer = ({
  projectId,
}: ImportLayoutContainerProps) => {
  const { token } = useConfig();
  const { log, clearLog } = useLog();

  const [layoutExport, setLayoutExport] = useState<LayoutExportJson | null>(null);
  const [layoutFileInfo, setLayoutFileInfo] = useState<LoadedFileInfo | null>(null);
  const [floorPlans, setFloorPlans] = useState<FloorPlanRecord[]>([]);
  const [loadingFloorPlans, setLoadingFloorPlans] = useState(false);
  const [floorPlanIdText, setFloorPlanIdText] = useState('');
  const [pageIndexText, setPageIndexText] = useState('0');
  const [running, setRunning] = useState(false);

  const floorPlanId = Number.parseInt(floorPlanIdText, 10);
  const pageIndex = Number.parseInt(pageIndexText, 10);
  const hasValidTarget =
    Number.isInteger(floorPlanId) && floorPlanId > 0 && Number.isInteger(pageIndex) && pageIndex >= 0;

  const handleLayoutLoaded = useCallback(
    (data: unknown, fileName: string) => {
      const json = data as LayoutExportJson;
      if (!json || typeof json !== 'object') {
        setLayoutExport(null);
        setLayoutFileInfo(null);
        log('err', `${fileName} is not a JSON object.`);
        return;
      }
      if (json.schema !== LAYOUT_SCHEMA) {
        log('warn', `${fileName}: schema is "${String(json.schema)}", expected "${LAYOUT_SCHEMA}".`);
      }
      if (json.status !== 'ok') {
        log(
          'err',
          `${fileName}: layout status is "${String(json.status)}"${json.reason ? ` (${json.reason})` : ''}; only an "ok" layout can be imported.`,
        );
      }
      setLayoutExport(json);
      setLayoutFileInfo({ fileName, stats: describeLayout(json) });
    },
    [log],
  );

  const fetchFloorPlans = useCallback(async () => {
    if (!projectId) {
      log('err', 'Project ID is required to fetch floor plans.');
      return;
    }
    setLoadingFloorPlans(true);
    try {
      const [activeResponse, archivedResponse] = await Promise.all([
        api<FloorPlanRecord[]>('GET', `/api/quantity-surveyor-floor-plans?projectId=${projectId}`, undefined, token),
        api<FloorPlanRecord[]>('GET', `/api/quantity-surveyor-floor-plans?projectId=${projectId}&archived=true`, undefined, token),
      ]);
      if (activeResponse.status === 200 && archivedResponse.status === 200) {
        setFloorPlans([...activeResponse.data, ...archivedResponse.data]);
        log('ok', `Found ${activeResponse.data.length} active and ${archivedResponse.data.length} archived floor plan(s) for project ${projectId}.`);
      } else {
        const failedResponse = activeResponse.status !== 200 ? activeResponse : archivedResponse;
        log('err', `Failed to fetch floor plans (${failedResponse.status}): ${JSON.stringify(failedResponse.data)}`);
      }
    } catch (error) {
      log('err', `Error fetching floor plans: ${(error as Error).message}`);
    } finally {
      setLoadingFloorPlans(false);
    }
  }, [projectId, token, log]);

  // The converted payload, so the summary shows what Monco will receive before the click.
  const preview = useMemo(() => {
    if (!layoutExport || layoutExport.status !== 'ok') return null;
    try {
      return buildLayoutImportPayload({ layoutExport, floorPlanId: 0, pageIndex: 0 });
    } catch {
      return null;
    }
  }, [layoutExport]);

  const summaryItems = useMemo<SummaryItem[]>(() => {
    if (!preview) return [];
    const { payload, skipped } = preview;
    const items: SummaryItem[] = [
      { label: `${payload.zones.length} zones`, variant: 'green' },
      { label: `${payload.rooms.length} rooms`, variant: 'green' },
      { label: `${payload.walls.length} walls`, variant: 'green' },
      { label: `${payload.doors.length} doors`, variant: 'green' },
      { label: `${payload.windows.length} windows`, variant: 'green' },
      {
        label: payload.metersPerPdfPoint !== undefined
          ? `scale ${payload.metersPerPdfPoint.toFixed(6)} m/pt (applied only if the page is uncalibrated)`
          : 'no verified scale (page calibration untouched)',
      },
    ];
    for (const [collection, count] of Object.entries(skipped)) {
      items.push({ label: `${count} ${collection} skipped (invalid geometry)` });
    }
    return items;
  }, [preview]);

  const handleImport = useCallback(async () => {
    if (running) return;
    if (!layoutExport) return log('err', 'Please load a layout JSON file first.');
    if (layoutExport.status !== 'ok') return log('err', `Layout status is "${String(layoutExport.status)}"; only an "ok" layout can be imported.`);
    if (!hasValidTarget) return log('err', 'Floor Plan ID must be a positive integer and Page Index a non-negative integer.');

    setRunning(true);
    clearLog();
    try {
      const { payload, skipped } = buildLayoutImportPayload({ layoutExport, floorPlanId, pageIndex });
      const skippedNote = Object.entries(skipped)
        .map(([collection, count]) => `${count} ${collection}`)
        .join(', ');
      log(
        'step',
        `Importing layout into floor plan #${floorPlanId} page ${pageIndex}: ${payload.zones.length} zones, ${payload.rooms.length} rooms, ${payload.walls.length} walls, ${payload.doors.length} doors, ${payload.windows.length} windows${skippedNote ? ` (skipped: ${skippedNote})` : ''}...`,
      );
      log('info', '  This replaces every room, zone, wall, door and window of that page, hand-drawn rooms included.');

      const response = await api<LayoutImportResult>(
        'POST',
        '/api/quantity-surveyor-floor-plan-layouts/import',
        payload,
        token,
      );
      if (response.status !== 200 && response.status !== 201) {
        log('err', `  Failed (${response.status}): ${JSON.stringify(response.data)}`);
        return;
      }
      const result = response.data;
      log(
        'ok',
        `\nDone! Created ${result.zonesCreated} zones, ${result.roomsCreated} rooms, ${result.wallsCreated} walls, ${result.doorsCreated} doors, ${result.windowsCreated} windows.`,
      );
      log(
        'info',
        result.scaleApplied
          ? `  Page scale applied; ${result.routesRecomputed} route(s) re-measured.`
          : '  Page scale left as it was.',
      );
    } catch (error) {
      log('err', `Unexpected error: ${(error as Error).message}`);
    } finally {
      setRunning(false);
    }
  }, [running, layoutExport, hasValidTarget, floorPlanId, pageIndex, token, log, clearLog]);

  return (
    <div>
      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
        <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
          Target Floor Plan
        </h2>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="block text-[12px] text-[#8b949e] mb-1">Project ID</label>
            <input type="text" className={inputClass} value={projectId} disabled style={{ width: 100 }} />
          </div>
          <button
            className={`${buttonClass} bg-[#1f6feb] hover:opacity-85`}
            onClick={fetchFloorPlans}
            disabled={loadingFloorPlans || !projectId}
          >
            {loadingFloorPlans ? 'Loading...' : 'Fetch Floor Plans'}
          </button>
          <div>
            <label className="block text-[12px] text-[#8b949e] mb-1">Floor Plan ID</label>
            <input
              type="number"
              min={1}
              className={inputClass}
              value={floorPlanIdText}
              onChange={(event) => setFloorPlanIdText(event.target.value)}
              placeholder="e.g. 123"
              style={{ width: 140 }}
            />
          </div>
          <div>
            <label className="block text-[12px] text-[#8b949e] mb-1">Page Index</label>
            <input
              type="number"
              min={0}
              className={inputClass}
              value={pageIndexText}
              onChange={(event) => setPageIndexText(event.target.value)}
              style={{ width: 100 }}
            />
          </div>
        </div>
        {floorPlans.length > 0 && (
          <div className="max-h-[260px] overflow-y-auto mt-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[#8b949e] text-left border-b border-[#30363d]">
                  <th className="py-2 px-3 w-[80px]">ID</th>
                  <th className="py-2 px-3">Name</th>
                  <th className="py-2 px-3 w-[180px]">Created</th>
                </tr>
              </thead>
              <tbody>
                {floorPlans.map((plan) => (
                  <tr
                    key={plan.id}
                    className={`border-b border-[#21262d] cursor-pointer transition-colors ${
                      floorPlanId === plan.id ? 'bg-[#1f6feb22] text-[#58a6ff]' : 'hover:bg-[#21262d]'
                    }`}
                    onClick={() => setFloorPlanIdText(String(plan.id))}
                  >
                    <td className="py-2 px-3 font-mono">{plan.id}</td>
                    <td className="py-2 px-3">
                      {plan.name ?? '(unnamed)'}
                      {plan.archivedAt !== null && <span className="ml-2 text-[#d29922]">(archived)</span>}
                    </td>
                    <td className="py-2 px-3 text-[#8b949e]">{new Date(plan.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
        <h2 className="text-sm text-[#8b949e] uppercase tracking-wide mb-3">
          Layout JSON
        </h2>
        <div className="flex gap-3 flex-wrap">
          <JsonFileInput
            label="Layout export (agent_output/layout/<file>/page_<n>.json)"
            placeholder="Drop or click to select layout JSON"
            onFileLoaded={handleLayoutLoaded}
            onError={(message) => log('err', message)}
            loadedInfo={layoutFileInfo}
          />
        </div>
        {summaryItems.length > 0 && <DataSummary items={summaryItems} />}
      </div>

      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4">
        <button
          className={`${buttonClass} bg-[#238636] hover:opacity-85`}
          onClick={handleImport}
          disabled={running || !preview || !hasValidTarget}
          title="Replaces the rooms, zones, walls, doors and windows of the target page with this layout"
        >
          {running ? 'Importing...' : 'Import Layout'}
        </button>
      </div>
    </div>
  );
};
